import { normalizeInternalRoute } from "../app/routing/routeParser";

/**
 * Sanitize the post-sign-in navigation target into a same-origin internal
 * path + search (NO hash, NO credentials).
 *
 * Accepts either the current URL's fragment (a legacy `#route=...&source=...`
 * hash, from which route/source are lifted into path/query) or a path-ish
 * string (the already-canonical `/settings`). Rules:
 *   - only same-origin internal paths are allowed; `//host`, an absolute URL,
 *     backslash, NUL, and control chars all fall back to `/`
 *   - the hash is never preserved (endpoint/token ride the hash and are
 *     thereby dropped)
 *   - default (empty/absent input) is `/`
 */
export function sanitizeAccountNext(input: string): string {
  const hashIdx = input.indexOf("#");
  const hash = hashIdx >= 0 ? input.slice(hashIdx) : "";
  const pre = hashIdx >= 0 ? input.slice(0, hashIdx) : input;

  const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
  const routeFromHash = hashParams.get("route");
  const sourceFromHash = hashParams.get("source");

  let base: string;
  if (routeFromHash) {
    base = normalizeInternalRoute(routeFromHash);
  } else if (pre) {
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(pre)) {
      // Absolute URL — only same-origin is acceptable.
      try {
        const u = new URL(pre);
        if (u.origin !== window.location.origin) return "/";
        base = normalizeInternalRoute(u.pathname + u.search);
      } catch {
        return "/";
      }
    } else {
      base = normalizeInternalRoute(pre);
    }
  } else {
    return "/";
  }

  if (sourceFromHash) {
    base +=
      (base.includes("?") ? "&" : "?") +
      `source=${encodeURIComponent(sourceFromHash)}`;
  }
  return base || "/";
}

/** Ato's own sign-in screen. `/account` is kept as an alias for older links,
 *  but every link this app mints uses `/sign-in`. */
export const ATO_SIGN_IN_ROUTE = "/sign-in";

export interface SignInInitiationResult {
  ok: boolean;
  /** Set only when `ok` is false. Kept in the shape callers already handle;
   *  a first-party navigation has no remote step that can fail, so this is
   *  always null today. */
  message: string | null;
}

/**
 * Send the user to Ato's OWN sign-in screen (Email OTP / passkey).
 *
 * This used to begin a GitHub OAuth round-trip. GitHub is no longer an Ato
 * identity provider — it is an optional Connection made from Settings after
 * you have an account — so "sign in" is now a plain in-app navigation to
 * `/sign-in`, with the current location saved as the post-sign-in
 * destination exactly as before.
 *
 * Still async and still returns `SignInInitiationResult` so the existing
 * callers (useAccountController's error banner, the inline button hooks)
 * keep working unchanged.
 */
export async function initiateSignIn(): Promise<SignInInitiationResult> {
  // Strip endpoint/token (they ride the hash, which is never saved) before
  // saving the next target; the stored value is restored after sign-in.
  const safeNext = sanitizeAccountNext(
    window.location.pathname + window.location.search + window.location.hash,
  );
  sessionStorage.setItem("ato_account_next", safeNext);

  const target = `${ATO_SIGN_IN_ROUTE}?next=${encodeURIComponent(safeNext)}`;
  try {
    window.location.assign(target);
  } catch {
    window.location.href = target;
  }
  return { ok: true, message: null };
}

/**
 * Build, but do not navigate to, the FIRST-PARTY sign-in URL — used by
 * AppPage.tsx's popup-based session-continuation flow, which must navigate a
 * *separate popup window* via `popup.location.replace(url)` and therefore
 * needs a literal navigable URL rather than an in-page navigation.
 *
 * `callbackURL` is the same same-origin `/auth/callback#continuation_*` URL
 * that flow already builds: its path becomes `?next=` (validated as an
 * internal route by the sign-in screen) and its fragment rides along on the
 * sign-in URL itself, so the screen can hand it straight back on success.
 * The popup therefore signs in with Email OTP or a passkey — no provider, no
 * third-party origin — and then lands on `/auth/callback`, which already
 * knows how to finish a continuation.
 */
export function buildAtoSignInUrl(callbackURL: string): string {
  const url = new URL(callbackURL, window.location.origin);
  const next = normalizeInternalRoute(url.pathname + url.search);
  return `${window.location.origin}${ATO_SIGN_IN_ROUTE}?next=${encodeURIComponent(next)}${url.hash}`;
}

export function consumeAccountNext(): string {
  const next = sessionStorage.getItem("ato_account_next");
  sessionStorage.removeItem("ato_account_next");
  // Re-sanitize defensively in case a stale value was stored by older code.
  // Sign-in-complete fallback (nothing stored) is /settings.
  return next ? sanitizeAccountNext(next) : "/settings";
}
